import React, { useEffect, useState } from "react";
import { Snackbar, Button, IconButton } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import InstallAppDialog from "components/common/InstallAppDialog";
import { useInstallApp } from "hooks/useInstallApp";

// How long the user stays before the nudge appears. Long enough that it never
// lands on top of someone who opened the dashboard to do one quick thing.
const DWELL_MS = 20000;

// ⚠️ THE UNPROMPTED NUDGE IS OFF IN PRODUCTION, ON PURPOSE.
//
// Installing is offered from the drawer ("Install app") and from the dialog it
// opens, both of which the user goes looking for. What is held back is only the
// Snackbar that appears at nobody's request while a clinician is working.
//
// Flip this to true to bring it back; nothing else has to change.
const NUDGE_ENABLED = false;

/**
 * The unprompted nudge to install the app, mounted once for the whole dashboard.
 *
 * It is a Snackbar rather than a dialog because the user did not ask for it.
 * Tapping through opens InstallAppDialog, which is where the actual steps live.
 *
 * Deliberately not mounted on VisitStart, CareStart, or clinic check-in: those
 * are one-off flows reached by scanning a code, and a patient part-way through
 * an intake form has no use for a home screen icon.
 */
const InstallAppPrompt = () => {
  const { nudgeAllowed, canPromptDirectly, promptInstall, dismiss } =
    useInstallApp();
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!NUDGE_ENABLED || !nudgeAllowed) return undefined;
    const timer = setTimeout(() => setOpen(true), DWELL_MS);
    return () => clearTimeout(timer);
  }, [nudgeAllowed]);

  const handleDismiss = () => {
    setOpen(false);
    dismiss();
  };

  const handleAccept = async () => {
    setOpen(false);
    // Chromium gave us an install event, so there is nothing to explain.
    if (canPromptDirectly) {
      await promptInstall();
      return;
    }
    setDialogOpen(true);
  };

  if (!NUDGE_ENABLED) return null;

  return (
    <>
      <Snackbar
        open={open}
        message="Add HealthDesk to your home screen"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={(_, reason) => {
          // A click elsewhere on the page is not an answer, so it is ignored
          // rather than counted as a dismissal.
          if (reason !== "clickaway") handleDismiss();
        }}
        action={
          <>
            <Button color="inherit" size="small" onClick={handleAccept}>
              {canPromptDirectly ? "Install" : "Show me how"}
            </Button>
            <IconButton
              size="small"
              color="inherit"
              aria-label="Dismiss"
              onClick={handleDismiss}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </>
        }
      />
      <InstallAppDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

export default InstallAppPrompt;
